/**
 * Agent 命令 - 使用 AI Agent 处理任务
 */

import { Command } from 'commander';
import { logger } from '../utils/logger';
import { withErrorHandling } from '../utils/error-handler';
import { AgentManager } from '../core/agent-manager';
import { TaskStore } from '../core/task-store';
import { SmartAgentAllocator } from '../core/smart-agent-allocator';
import { colors } from '../utils/colors';
import { Table } from 'console-table-printer';
import ora from 'ora';
import path from 'path';
import fs from 'fs-extra';

export function createAgentCommand(): Command {
  const command = new Command('agent');
  
  command
    .description('Use AI agents to work on tasks')
    .addCommand(createAgentListCommand())
    .addCommand(createAgentExecuteCommand())
    .addCommand(createAgentRecommendCommand())
    .addCommand(createAgentInfoCommand());
  
  return command;
}

/**
 * agent list - 列出所有可用的 Agent
 */
function createAgentListCommand(): Command {
  const command = new Command('list');
  
  command
    .description('List all available agents')
    .alias('ls')
    .action(withErrorHandling(async () => {
      const manager = new AgentManager();
      await manager.initialize();
      
      const agents = manager.getAgents();
      
      if (agents.length === 0) {
        console.log(colors.warning('No agents found.'));
        console.log(colors.info('Agent configurations should be in .workflow/agents/'));
        return;
      }
      
      console.log(colors.header('\n🤖 Available AI Agents\n'));
      
      const table = new Table({
        columns: [
          { name: 'id', title: 'ID', alignment: 'left' },
          { name: 'name', title: 'Name', alignment: 'left' },
          { name: 'avatar', title: '🎭', alignment: 'center' },
          { name: 'category', title: 'Category', alignment: 'center', colored: true },
          { name: 'capabilities', title: 'Capabilities', alignment: 'left', maxLen: 40 }
        ]
      });
      
      for (const agent of agents) {
        const caps = agent.capabilities
          .filter(c => c.required)
          .map(c => c.name)
          .join(', ');
          
        table.addRow({
          id: agent.id,
          name: agent.name,
          avatar: agent.avatar,
          category: agent.category.toUpperCase(),
          capabilities: caps.length > 40 ? caps.substring(0, 37) + '...' : caps
        });
      }
      
      table.printTable();
      
      console.log(colors.dim('\n💡 Tips:'));
      console.log(colors.dim('• Use ') + colors.command('workflow agent <agent-id> <task>') + colors.dim(' to execute a task'));
      console.log(colors.dim('• Use ') + colors.command('workflow agent info <agent-id>') + colors.dim(' to see agent details'));
    }));
  
  return command;
}

/**
 * agent execute - 使用指定 Agent 执行任务
 */
function createAgentExecuteCommand(): Command {
  const command = new Command('execute');
  
  command
    .description('Execute a task using a specific agent')
    .argument('<agent>', 'Agent ID (developer/reviewer/writer)')
    .argument('<task>', 'Task ID or description')
    .option('--auto-save', 'Automatically save agent output', false)
    .option('--with-suggestions', 'Include agent suggestions', true)
    .alias('exec')
    .action(withErrorHandling(async (agentId: string, taskInput: string, options) => {
      const manager = new AgentManager();
      await manager.initialize();
      
      const taskStore = new TaskStore();
      
      // 获取 Agent
      const agent = manager.getAgent(agentId);
      if (!agent) {
        console.log(colors.error(`Agent not found: ${agentId}`));
        console.log(colors.info('Available agents: developer, reviewer, writer'));
        return;
      }
      
      // 获取或创建任务
      let task;
      const tasks = await taskStore.getTasks();
      task = tasks.find(t => t.id === taskInput);
      
      if (!task) {
        // 如果不是 ID，作为新任务创建
        task = await taskStore.createTask({
          content: taskInput,
          priority: 'medium'
        });
        console.log(colors.success(`Created new task: ${task.id}`));
      }
      
      console.log(colors.header(`\n${agent.avatar} ${agent.name} is working on:\n`));
      console.log(colors.info(task.content));
      console.log();
      
      // 执行任务
      const spinner = ora('Agent thinking...').start();
      
      try {
        const result = await manager.executeTask(agentId, {
          task,
          projectContext: {
            language: 'TypeScript',
            framework: 'React'
          }
        });
        
        spinner.succeed('Agent completed the task!');
        
        // 显示输出
        console.log(colors.header('\n📋 Agent Output:\n'));
        console.log(result.output);
        
        // 显示建议
        if (options.withSuggestions && result.suggestions) {
          console.log(colors.header('\n💡 Suggestions:\n'));
          result.suggestions.forEach((s, i) => {
            console.log(`${i + 1}. ${s}`);
          });
        }
        
        // 显示下一步
        if (result.nextSteps) {
          console.log(colors.header('\n🚀 Next Steps:\n'));
          result.nextSteps.forEach((s, i) => {
            console.log(`${i + 1}. ${s}`);
          });
        }
        
        // 显示生成的文件
        if (result.artifacts && result.artifacts.length > 0) {
          console.log(colors.header('\n📁 Generated Artifacts:\n'));
          const artifactTable = new Table({
            columns: [
              { name: 'type', title: 'Type', alignment: 'center', colored: true },
              { name: 'path', title: 'Path', alignment: 'left' }
            ]
          });
          
          for (const artifact of result.artifacts) {
            artifactTable.addRow({
              type: artifact.type.toUpperCase(),
              path: artifact.path
            });
          }
          
          artifactTable.printTable();
          
          if (options.autoSave) {
            // TODO: 实际保存文件
            console.log(colors.success('\n✅ Artifacts saved to project'));
          }
        }
        
        // 协作建议
        const collaborators = manager.getCollaborationSuggestions(agent, task);
        if (collaborators.length > 0) {
          console.log(colors.header('\n🤝 Suggested Collaboration:\n'));
          console.log('This agent works well with:');
          collaborators.forEach(c => {
            console.log(`• ${c.avatar} ${c.name} - ${c.description}`);
          });
        }
        
        // 更新任务状态
        if (agentId === 'reviewer' && task.status === 'review') {
          await taskStore.updateTask(task.id, { status: 'done' });
          console.log(colors.success('\n✅ Task marked as done after review'));
        }
        
      } catch (error: any) {
        spinner.fail('Agent execution failed');
        throw error;
      }
    }));
  
  // 添加快捷方式
  command.alias('e');
  
  return command;
}

/**
 * agent recommend - 为任务推荐合适的 Agent
 */
function createAgentRecommendCommand(): Command {
  const command = new Command('recommend');
  
  command
    .description('Get agent recommendation for a task')
    .argument('[task]', 'Task ID or current task if not specified')
    .option('--enable-memory', 'Enable memory system for intelligent recommendations', false)
    .option('--user-id <id>', 'User ID for memory system')
    .alias('rec')
    .action(withErrorHandling(async (taskId?: string, options) => {
      const manager = new AgentManager();
      await manager.initialize();
      
      const taskStore = new TaskStore();
      
      // 获取任务
      let task;
      if (taskId) {
        const tasks = await taskStore.getTasks();
        task = tasks.find(t => t.id === taskId);
        if (!task) {
          console.log(colors.error(`Task not found: ${taskId}`));
          return;
        }
      } else {
        // 获取当前任务
        const current = await taskStore.getCurrentTask();
        task = current;
        if (!task) {
          console.log(colors.warning('No current task'));
          return;
        }
      }
      
      console.log(colors.header('\n📋 Task Analysis:\n'));
      console.log(`Task: ${task.content}`);
      console.log(`Type: ${task.type || task.metadata?.type || 'general'}`);
      console.log(`Priority: ${task.priority}`);
      
      // 获取项目配置
      let projectId: string | undefined;
      const configPath = path.join(process.cwd(), '.workflow', 'config', 'project.json');
      if (await fs.pathExists(configPath)) {
        const config = await fs.readJSON(configPath);
        projectId = config.id;
      }
      
      // 使用智能 Agent 分配器
      if (options.enableMemory || process.env.WORKFLOW_ENABLE_MEMORY === 'true') {
        console.log(colors.info('\n🤖 Using AI-powered agent recommendation...'));
        
        const allocator = new SmartAgentAllocator({
          enableMemory: true,
          userId: options.userId || process.env.USER || 'user',
          projectId: projectId,
        });
        
        const allocation = await allocator.allocateAgents(task);
        
        console.log(colors.header('\n🎯 AI Recommendation:\n'));
        console.log(`Primary Agent: ${colors.success(allocation.primary)}`);
        if (allocation.reviewers && allocation.reviewers.length > 0) {
          console.log(`Reviewers: ${allocation.reviewers.join(', ')}`);
        }
        if (allocation.consultants && allocation.consultants.length > 0) {
          console.log(`Consultants: ${allocation.consultants.join(', ')}`);
        }
        if (allocation.reason) {
          console.log(`\nReason: ${colors.dim(allocation.reason)}`);
        }
        if (allocation.confidence) {
          const conf = Math.round(allocation.confidence * 100);
          console.log(`Confidence: ${conf >= 80 ? colors.success(conf + '%') : conf >= 60 ? colors.warning(conf + '%') : colors.error(conf + '%')}`);
        }
        if (allocation.memoryBased) {
          console.log(colors.info('\n✨ This recommendation is based on historical performance data'));
        }
        
        console.log(colors.header('\n🚀 Quick Start:\n'));
        console.log(colors.command(`workflow agent ${allocation.primary} ${task.id}`));
        
      } else {
        // 使用传统规则推荐
        const recommended = manager.recommendAgent(task);
        
        if (recommended) {
          console.log(colors.header('\n🎯 Recommended Agent:\n'));
          console.log(`${recommended.avatar} ${colors.success(recommended.name)}`);
          console.log(colors.dim(recommended.description));
          console.log();
          console.log('Key capabilities:');
          recommended.capabilities
            .filter(c => c.required)
            .forEach(c => {
              console.log(`• ${c.name} - ${c.description}`);
            });
          
          console.log(colors.header('\n🚀 Quick Start:\n'));
          console.log(colors.command(`workflow agent ${recommended.id} ${task.id}`));
        } else {
          console.log(colors.warning('\nNo specific agent recommendation'));
          console.log('You can use any of the available agents.');
        }
      }
    }));
  
  return command;
}

/**
 * agent info - 显示 Agent 详细信息
 */
function createAgentInfoCommand(): Command {
  const command = new Command('info');
  
  command
    .description('Show detailed information about an agent')
    .argument('<agent>', 'Agent ID')
    .action(withErrorHandling(async (agentId: string) => {
      const manager = new AgentManager();
      await manager.initialize();
      
      const agent = manager.getAgent(agentId);
      if (!agent) {
        console.log(colors.error(`Agent not found: ${agentId}`));
        return;
      }
      
      console.log(colors.header(`\n${agent.avatar} ${agent.name}\n`));
      console.log(colors.dim(agent.description));
      console.log();
      
      // 基本信息
      console.log(colors.info('Basic Information:'));
      console.log(`• ID: ${agent.id}`);
      console.log(`• Category: ${agent.category}`);
      console.log(`• Version: ${agent.version}`);
      console.log(`• Status: ${agent.status}`);
      console.log();
      
      // 能力
      console.log(colors.info('Capabilities:'));
      agent.capabilities.forEach(cap => {
        const req = cap.required ? colors.success(' [Required]') : '';
        console.log(`• ${cap.name}${req}`);
        console.log(colors.dim(`  ${cap.description}`));
      });
      console.log();
      
      // 工具
      console.log(colors.info('Tools & Technologies:'));
      const toolTable = new Table({
        columns: [
          { name: 'tool', title: 'Tool', alignment: 'left' },
          { name: 'type', title: 'Type', alignment: 'center', colored: true }
        ]
      });
      
      agent.tools.forEach(tool => {
        toolTable.addRow({
          tool: tool.name,
          type: tool.type.toUpperCase()
        });
      });
      
      toolTable.printTable();
      
      // 协作
      if (agent.collaboration) {
        console.log(colors.info('\nCollaboration:'));
        
        if (agent.collaboration.worksWith) {
          console.log('Works with:');
          agent.collaboration.worksWith.forEach(id => {
            console.log(`• ${id}`);
          });
        }
        
        if (agent.collaboration.provides) {
          console.log('\nProvides:');
          agent.collaboration.provides.forEach(item => {
            console.log(`• ${item}`);
          });
        }
        
        if (agent.collaboration.requires) {
          console.log('\nRequires:');
          agent.collaboration.requires.forEach(item => {
            console.log(`• ${item}`);
          });
        }
      }
      
      // 使用示例
      console.log(colors.header('\n💡 Usage Examples:\n'));
      console.log('Execute a task:');
      console.log(colors.command(`  workflow agent ${agent.id} "Your task description"`));
      console.log('\nExecute current task:');
      console.log(colors.command(`  workflow agent ${agent.id} $(workflow now --id)`));
    }));
  
  return command;
}